import type { SpotifyAuthStatus } from '@hitster/shared';
import { useT } from '../i18n.tsx';
import { logoutSpotify, refreshSpotifyProfile, startSpotifyLogin } from '../net/spotify-auth.ts';
import { ScopeWarning } from './ScopeWarning.tsx';

/**
 * Verbindungsstatus zu Spotify plus Login/Logout.
 *
 * Der Premium-Hinweis ist bewusst prominent: es ist die haeufigste
 * Ursache dafuer, dass "nichts passiert", wenn man Musik erwartet.
 */
export function SpotifyBar({
  status,
  onChanged,
  showPremiumWarning = true,
}: {
  status: SpotifyAuthStatus | null;
  onChanged: () => void;
  showPremiumWarning?: boolean;
}) {
  const t = useT();

  if (!status) {
    return <span className="text-dim text-sm">{t('common.loading')}</span>;
  }

  if (!status.linked) {
    return (
      <button
        type="button"
        className="btn btn--spotify"
        onClick={() => startSpotifyLogin(window.location.pathname)}
      >
        {t('spotify.connect')}
      </button>
    );
  }

  return (
    <div className="stack stack--tight">
      <div className="row">
        <span className="text-sm">
          {t('spotify.connected', { name: status.displayName ?? '—' })}
        </span>
        {status.hasPremium ? (
          <span className="badge badge--premium">Premium</span>
        ) : (
          <span className="badge">{t('spotify.notPremium', { product: status.productType ?? '—' })}</span>
        )}
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => {
            void logoutSpotify().then(onChanged);
          }}
        >
          {t('spotify.disconnect')}
        </button>
      </div>

      {/* Fehlende Scopes sind die haeufigste Ursache fuer 403-Fehler an
          voellig unverdaechtigen Stellen ("Playlist nicht zugaenglich"). */}
      <ScopeWarning status={status} onChanged={onChanged} />

      {showPremiumWarning && !status.hasPremium && (
        <div className="alert alert--warning">
          <div className="stack stack--tight">
            <strong>{t('spotify.premiumRequired')}</strong>
            <span className="text-sm">{t('spotify.premiumExplain')}</span>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                // Nach einem Premium-Kauf ist das Profil im Cache veraltet.
                void refreshSpotifyProfile().then(onChanged).catch(onChanged);
              }}
            >
              {t('spotify.refreshProfile')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
